import React, { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Button } from '@gx-design/button';

export const Slider = ({ className, items, itemsId, itemRender, leftArrow, rightArrow, rightBtnWrapWidth, sticky }) => {
  const [marginLeft, setMarginLeft] = useState(0);
  const [activeElement, setActiveElement] = useState(1);
  const [leftBtn, setLeftBtn] = useState(null);
  const [rightBtn, setRightBtn] = useState(null);
  const leftBtnRef = useRef();
  const rightBtnRef = useRef();
  const sliderRef = useRef();
  const sliderContentRef = useRef();

  const itemRefs = items.reduce((acc, item, index) => {
    acc[index] = useRef();
    return acc;
  }, {});

  const handleLeftClicked = () => {
    const sliderWidth = sliderRef.current.offsetWidth;
    let nextMarginLeft;

    if (marginLeft > sliderWidth) {
      nextMarginLeft = marginLeft - sliderWidth;
    } else {
      nextMarginLeft = 0;
    }

    setMarginLeft(nextMarginLeft);
  };

  const handleRightClicked = () => {
    const sliderWidth = sliderRef.current.offsetWidth;
    const contentWidth = sliderContentRef.current.offsetWidth;
    const remainingWidth = contentWidth - sliderWidth + rightBtnWrapWidth - marginLeft;
    let nextMarginLeft;

    if (remainingWidth > 0) {
      if (remainingWidth <= sliderWidth) {
        nextMarginLeft = marginLeft + remainingWidth;
      } else {
        nextMarginLeft = marginLeft + sliderWidth;
      }
    } else {
      nextMarginLeft = marginLeft;
    }

    setMarginLeft(nextMarginLeft);
  };

  const renderLeftBtn = () => {
    if (marginLeft !== 0) {
      setLeftBtn(
        <Button size="small" iconOnly onClick={handleLeftClicked} ref={leftBtnRef}>
          {leftArrow}
        </Button>
      );
    } else {
      setLeftBtn(null);
    }
  };

  const renderRightBtn = () => {
    const sliderWidth = sliderRef ? sliderRef.current.offsetWidth : 0;
    const contentWidth = sliderContentRef ? sliderContentRef.current.offsetWidth : 0;
    const remainingWidth = contentWidth - sliderWidth + rightBtnWrapWidth - marginLeft;

    if (remainingWidth > 0) {
      setRightBtn(
        <Button size="small" iconOnly onClick={handleRightClicked} ref={rightBtnRef}>
          {rightArrow}
        </Button>
      );
    } else {
      setRightBtn(null);
    }
  };

  const handleItemVisibility = (index) => {
    const itemRect = itemRefs[index].current.getBoundingClientRect();
    const leftBtnRect = leftBtnRef && leftBtnRef.current && leftBtnRef.current.getBoundingClientRect();
    const rightBtnRect = rightBtnRef && rightBtnRef.current && rightBtnRef.current.getBoundingClientRect();

    /** simulate button click if section item is out of the slider's range */
    if (rightBtnRect && itemRect.x + itemRect.width > rightBtnRect.x + rightBtnRect.width) {
      handleRightClicked();
    } else if (leftBtnRect && itemRect.x < leftBtnRect.x) {
      handleLeftClicked();
    }
  };

  const handleItemClick = (index) => {
    window.location.href = `#${itemsId}_${index + 1}`;
    setActiveElement(index + 1);
    handleItemVisibility(index);
  };

  const handleScroll = () => {
    const elements = document.querySelectorAll(`*[id^="${itemsId}_"]`);

    for (let i = 0; i < elements.length; i++) {
      const id = elements[i].id.split('_');
      const { height } = elements[i].getBoundingClientRect();

      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight) {
        handleItemVisibility(elements.length - 1);
        setActiveElement(elements.length);
      } else if (elements[i].offsetTop <= window.scrollY && elements[i].offsetTop + height > window.scrollY) {
        handleItemVisibility(i);
        setActiveElement(parseInt(id[1]));
        break;
      }
    }
  };

  const renderButtons = () => {
    renderRightBtn();
    renderLeftBtn();
  };

  useEffect(() => {
    window.addEventListener('resize', renderButtons);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', renderButtons);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    renderButtons();
  }, [marginLeft]);

  return (
    <div className={classNames([className, sticky ? 'gx-nav-sliderSticky' : 'gx-nav-slider'])} ref={sliderRef}>
      <span className="gx-nav-slider__btn-wrap gx-nav-slider__btn-wrap--prev">{leftBtn}</span>
      <div className="gx-nav-slider__wrap">
        <div
          className="gx-nav-slider__content"
          ref={sliderContentRef}
          style={{ marginLeft: `-${marginLeft}px` }}
        >
          <ul>
            {items.map((item, index) =>
              <li key={`slider_item_${index}`} ref={itemRefs[index]}
                onClick={() => handleItemClick(index)} className={activeElement === index + 1 ? 'is-selected' : null}>
                {itemRender(index, item)}
              </li>)}
          </ul>
        </div>
      </div>
      <span className="gx-nav-slider__btn-wrap gx-nav-slider__btn-wrap--next">{rightBtn}</span>
    </div>
  );
};

Slider.propTypes = {
  className: PropTypes.string,
  items: PropTypes.array.isRequired,
  itemsId: PropTypes.string,
  itemRender: PropTypes.func,
  leftArrow: PropTypes.node,
  rightArrow: PropTypes.node,
  sticky: PropTypes.bool,
};

Slider.defaultProps = {
  itemsId: 'item',
  itemRender: (index, item) => item.label,
  leftArrow: <span>&larr;</span>,
  rightArrow: <span>&rarr;</span>,
  sticky: false,
};
